import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { ratePlanService } from 'api/services/ratePlanService';
import { propertyService } from 'api/services/propertyService';
import { roomCategoryService } from 'api/services/roomCategoryService';
import { mealPlanService } from 'api/services/mealPlanService';
import type { RatePlan, CreateRatePlanRequest, UpdateRatePlanRequest, RatePlanDetailEntry } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface RatePlanModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: RatePlan | null;
  onSave: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, ratePlanId?: string) => void;
}

/** Internal row for the meal plan rates table */
interface MealPlanDetailRow {
  mealPlanId: string;
  mealPlanCode: string;
  mealPlanName: string;
  included: boolean;
  mealAdultRate: string;
  mealChildRate: string;
  isDefault: boolean;
  detailId?: string;
  rowVersion?: number;
  defaultAdultRate?: number;
  defaultChildRate?: number;
}

interface RatePlanFormData {
  rateName: string;
  rateType: string;
  propertyId: string;
  roomCategoryId: string;
  baseRate: string;
  minimumRate: string;
  extraAdultRate: string;
  extraChildRate: string;
  childAgeLimit: string;
  chargeableChildAge: string;
  infantAgeLimit: string;
  validFrom: string;
  validTo: string;
  dayOfWeek: string[];
  minNights: string;
  maxNights: string;
  advanceBookingDays: string;
  closeToArrival: boolean;
  closeToDeparture: boolean;
  includeTaxes: boolean;
  isActive: boolean;
  mealPlanDetails: MealPlanDetailRow[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: RatePlanFormData = {
  rateName: '',
  rateType: '',
  propertyId: '',
  roomCategoryId: '',
  baseRate: '',
  minimumRate: '',
  extraAdultRate: '',
  extraChildRate: '',
  childAgeLimit: '12',
  chargeableChildAge: '6',
  infantAgeLimit: '',
  validFrom: '',
  validTo: '',
  dayOfWeek: [],
  minNights: '',
  maxNights: '',
  advanceBookingDays: '',
  closeToArrival: false,
  closeToDeparture: false,
  includeTaxes: false,
  isActive: true,
  mealPlanDetails: [],
};

const RATE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Rack', label: 'Rack' },
  { value: 'Standard', label: 'Standard' },
  { value: 'Corporate', label: 'Corporate' },
  { value: 'Agent', label: 'Agent' },
  { value: 'OTA', label: 'OTA' },
  { value: 'Promotional', label: 'Promotional' },
  { value: 'Weekend', label: 'Weekend' },
  { value: 'Seasonal', label: 'Seasonal' },
];

const DAY_OPTIONS: SelectOption[] = [
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
  { value: 'Sun', label: 'Sunday' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const RatePlanModal: React.FC<RatePlanModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  const [formData, setFormData] = useState<RatePlanFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  const [propertyOptions, setPropertyOptions] = useState<SelectOption[]>([]);
  const [roomCategoryOptions, setRoomCategoryOptions] = useState<SelectOption[]>([]);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // --- "Booking Rules" collapsible (Min/Max Nights, Advance Booking Days,
  //     Close-to-Arrival/Departure, Tax Inclusive). Auto-expands on edit when
  //     any value is non-default. None are required. ---
  const [rulesOpen, setRulesOpen] = useState(false);

  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<RatePlan | null | undefined>(record);

  // --- Load dropdown options + meal plans ---
  useEffect(() => {
    if (!isOpen) return;
    const loadOptions = async () => {
      try {
        const [propRes, rcRes, mpRes] = await Promise.all([
          propertyService.getAll({ pageSize: 500, includeInactive: false }),
          roomCategoryService.getAll({ pageSize: 500, includeInactive: false }),
          mealPlanService.getAll({ pageSize: 500, includeInactive: false }),
        ]);
        setPropertyOptions(
          propRes.data.map(p => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }))
        );
        setRoomCategoryOptions(
          rcRes.data.map(rc => ({ value: rc.id, label: `${rc.categoryCode} - ${rc.categoryName}` }))
        );

        // Build meal plan detail rows
        const allMealPlans = mpRes.data;
        const rec = recordRef.current;
        const isEditOrCopy = (modeRef.current === 'edit' || modeRef.current === 'copy') && rec;

        // For edit/copy: fetch full record with details
        let recordDetails: RatePlan['details'] = undefined;
        if (isEditOrCopy && rec) {
          try {
            const fullRec = await ratePlanService.getById(rec.id);
            recordDetails = fullRec.data?.details;
            if (modeRef.current === 'edit') {
              effectiveRecordRef.current = fullRec.data;
            }
          } catch { /* use record as-is */ }
        }

        const detailMap = new Map<string, NonNullable<RatePlan['details']>[number]>();
        if (recordDetails) {
          recordDetails.forEach(d => detailMap.set(d.mealPlanId, d));
        }

        const rows: MealPlanDetailRow[] = allMealPlans.map(mp => {
          const existingDetail = detailMap.get(mp.id);
          if (existingDetail && isEditOrCopy) {
            return {
              mealPlanId: mp.id,
              mealPlanCode: mp.planCode,
              mealPlanName: mp.planName,
              included: true,
              mealAdultRate: String(existingDetail.mealAdultRate ?? 0),
              mealChildRate: String(existingDetail.mealChildRate ?? 0),
              isDefault: existingDetail.isDefaultMealPlan,
              detailId: modeRef.current === 'edit' ? existingDetail.id : undefined,
              rowVersion: modeRef.current === 'edit' ? existingDetail.rowVersion : undefined,
              defaultAdultRate: mp.adultRate,
              defaultChildRate: mp.childRate,
            };
          }
          return {
            mealPlanId: mp.id,
            mealPlanCode: mp.planCode,
            mealPlanName: mp.planName,
            included: false,
            mealAdultRate: '',
            mealChildRate: '',
            isDefault: false,
            defaultAdultRate: mp.adultRate,
            defaultChildRate: mp.childRate,
          };
        });

        // Remove any details that are on the record but not in current active meal plans (inactive)
        if (recordDetails) {
          const activeMealPlanIds = new Set(allMealPlans.map(mp => mp.id));
          recordDetails.forEach(d => {
            if (!activeMealPlanIds.has(d.mealPlanId)) {
              rows.push({
                mealPlanId: d.mealPlanId,
                mealPlanCode: d.mealPlanCode || '?',
                mealPlanName: d.mealPlanName ? `${d.mealPlanName} (Inactive)` : '(Inactive)',
                included: true,
                mealAdultRate: String(d.mealAdultRate ?? 0),
                mealChildRate: String(d.mealChildRate ?? 0),
                isDefault: d.isDefaultMealPlan,
                detailId: modeRef.current === 'edit' ? d.id : undefined,
                rowVersion: modeRef.current === 'edit' ? d.rowVersion : undefined,
              });
            }
          });
        }

        // Only update mealPlanDetails if NOT restoring
        if (!isRestoreRef.current) {
          setFormData(prev => ({ ...prev, mealPlanDetails: rows }));
          // Update snapshot after rows are set
          setTimeout(() => {
            setFormData(prev => {
              initialSnapshotRef.current = JSON.stringify(prev);
              return prev;
            });
          }, 0);
        }
      } catch { /* ignore */ }
    };
    loadOptions();
  }, [isOpen]);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    let data: RatePlanFormData;
    if ((mode === 'edit' || mode === 'copy') && rec) {
      data = {
        rateName: rec.rateName || '',
        rateType: rec.rateType || '',
        propertyId: rec.propertyId || '',
        roomCategoryId: rec.roomCategoryId || '',
        baseRate: rec.baseRate != null ? String(rec.baseRate) : '',
        minimumRate: rec.minimumRate != null ? String(rec.minimumRate) : '',
        extraAdultRate: rec.extraAdultRate != null ? String(rec.extraAdultRate) : '',
        extraChildRate: rec.extraChildRate != null ? String(rec.extraChildRate) : '',
        childAgeLimit: rec.childAgeLimit != null ? String(rec.childAgeLimit) : '',
        chargeableChildAge: rec.chargeableChildAge != null ? String(rec.chargeableChildAge) : '',
        infantAgeLimit: rec.infantAgeLimit != null ? String(rec.infantAgeLimit) : '',
        validFrom: rec.validFrom ? rec.validFrom.split('T')[0] : '',
        validTo: rec.validTo ? rec.validTo.split('T')[0] : '',
        dayOfWeek: rec.dayOfWeek ? rec.dayOfWeek.split(',').map(d => d.trim()) : [],
        minNights: rec.minNights != null ? String(rec.minNights) : '',
        maxNights: rec.maxNights != null ? String(rec.maxNights) : '',
        advanceBookingDays: rec.advanceBookingDays != null ? String(rec.advanceBookingDays) : '',
        closeToArrival: rec.closeToArrival ?? false,
        closeToDeparture: rec.closeToDeparture ?? false,
        includeTaxes: rec.includeTaxes ?? false,
        isActive: rec.isActive,
        mealPlanDetails: [], // Will be populated by the loadOptions effect
      };
    } else {
      data = { ...INITIAL_FORM_DATA, dayOfWeek: [], mealPlanDetails: [] };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setRulesOpen(!!(data.minNights || data.maxNights || data.advanceBookingDays ||
      data.closeToArrival || data.closeToDeparture || data.includeTaxes));
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const name = currentFormData.rateName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = name ? `Add - ${name} (${idx})` : `Add Rate Plan (${idx})`;
    } else if (currentMode === 'copy') {
      const name = currentFormData.rateName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.rateName || 'Rate Plan'}`;
    }
    const savedFormData = {
      ...currentFormData,
      dayOfWeek: [...currentFormData.dayOfWeek],
      mealPlanDetails: currentFormData.mealPlanDetails.map(r => ({ ...r })),
    };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? recordRef.current?.id : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Meal plan detail row handlers ---
  const handleToggleInclude = useCallback((mealPlanId: string) => {
    clearFieldError('mealPlanDetails');
    setFormData(prev => ({
      ...prev,
      mealPlanDetails: prev.mealPlanDetails.map(row => {
        if (row.mealPlanId !== mealPlanId) return row;
        const newIncluded = !row.included;
        return {
          ...row,
          included: newIncluded,
          mealAdultRate: newIncluded && !row.mealAdultRate && row.defaultAdultRate != null ? String(row.defaultAdultRate) : (newIncluded ? row.mealAdultRate : ''),
          mealChildRate: newIncluded && !row.mealChildRate && row.defaultChildRate != null ? String(row.defaultChildRate) : (newIncluded ? row.mealChildRate : ''),
          isDefault: newIncluded ? row.isDefault : false,
        };
      }),
    }));
  }, [clearFieldError]);

  const handleSetDefault = useCallback((mealPlanId: string) => {
    clearFieldError('mealPlanDetails');
    setFormData(prev => ({
      ...prev,
      mealPlanDetails: prev.mealPlanDetails.map(row => ({
        ...row,
        isDefault: row.mealPlanId === mealPlanId,
      })),
    }));
  }, [clearFieldError]);

  const handleDetailRateChange = useCallback((mealPlanId: string, field: 'mealAdultRate' | 'mealChildRate', value: string) => {
    setFormData(prev => ({
      ...prev,
      mealPlanDetails: prev.mealPlanDetails.map(row =>
        row.mealPlanId === mealPlanId ? { ...row, [field]: value } : row
      ),
    }));
  }, []);

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.rateName.trim()) {
      errors.rateName = 'Rate Name is required';
    } else if (formData.rateName.length > 100) {
      errors.rateName = 'Rate Name cannot exceed 100 characters';
    }

    if (!formData.rateType) {
      errors.rateType = 'Rate Type is required';
    } else if (formData.rateType.length > 30) {
      errors.rateType = 'Rate Type cannot exceed 30 characters';
    }

    if (!formData.propertyId) errors.propertyId = 'Property is required';
    if (!formData.roomCategoryId) errors.roomCategoryId = 'Room Category is required';
    if (!formData.baseRate) errors.baseRate = 'Base Rate is required';

    // Validate meal plan details
    const includedRows = formData.mealPlanDetails.filter(r => r.included);
    if (includedRows.length === 0) {
      errors.mealPlanDetails = 'At least one meal plan must be included';
    } else {
      const defaultCount = includedRows.filter(r => r.isDefault).length;
      if (defaultCount === 0) errors.mealPlanDetails = 'One meal plan must be marked as default';
      if (defaultCount > 1) errors.mealPlanDetails = 'Only one meal plan can be default';

      // Validate meal rates on included rows
      for (const row of includedRows) {
        if (row.mealAdultRate) {
          const val = parseFloat(row.mealAdultRate);
          if (isNaN(val) || val < 0) {
            errors.mealPlanDetails = `Meal Adult Rate for ${row.mealPlanCode} must be >= 0`;
            break;
          }
        }
        if (row.mealChildRate) {
          const val = parseFloat(row.mealChildRate);
          if (isNaN(val) || val < 0) {
            errors.mealPlanDetails = `Meal Child Rate for ${row.mealPlanCode} must be >= 0`;
            break;
          }
        }
      }
    }

    // Validate numeric fields
    const numericFields: { key: keyof RatePlanFormData; label: string }[] = [
      { key: 'baseRate', label: 'Base Rate' },
      { key: 'minimumRate', label: 'Minimum Rate' },
      { key: 'extraAdultRate', label: 'Extra Adult Rate' },
      { key: 'extraChildRate', label: 'Extra Child Rate' },
    ];
    for (const { key, label } of numericFields) {
      const val = formData[key] as string;
      if (val && !errors[key]) {
        const num = parseFloat(val);
        if (isNaN(num) || num < 0) errors[key] = `${label} must be a positive number`;
      }
    }

    // Validate integer fields
    const intFields: { key: keyof RatePlanFormData; label: string }[] = [
      { key: 'childAgeLimit', label: 'Child Age Limit' },
      { key: 'chargeableChildAge', label: 'Chargeable Child Age' },
      { key: 'infantAgeLimit', label: 'Infant Age Limit' },
      { key: 'minNights', label: 'Min Nights' },
      { key: 'maxNights', label: 'Max Nights' },
      { key: 'advanceBookingDays', label: 'Advance Booking Days' },
    ];
    for (const { key, label } of intFields) {
      const val = formData[key] as string;
      if (val) {
        const num = parseInt(val, 10);
        if (isNaN(num) || num < 0) errors[key] = `${label} must be a positive integer`;
      }
    }

    if (formData.validFrom && formData.validTo) {
      if (new Date(formData.validFrom) > new Date(formData.validTo)) {
        errors.validTo = 'Valid To must be after Valid From';
      }
    }

    if (formData.minNights && formData.maxNights) {
      const min = parseInt(formData.minNights, 10);
      const max = parseInt(formData.maxNights, 10);
      if (!isNaN(min) && !isNaN(max) && min > max) errors.maxNights = 'Max Nights must be greater than Min Nights';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    // Async duplicate rate name check
    setIsValidating(true);
    try {
      const existing = await ratePlanService.getAll({ searchTerm: formData.rateName.trim(), pageSize: 25, includeInactive: false });
      const duplicate = existing.data.find(r =>
        r.rateName.trim().toLowerCase() === formData.rateName.trim().toLowerCase() &&
        r.roomCategoryId === formData.roomCategoryId &&
        (mode !== 'edit' || r.id !== record?.id)
      );
      if (duplicate) {
        setFieldErrors({ rateName: 'A rate plan with this name already exists for the selected room category' });
        setIsValidating(false);
        return;
      }
    } catch { /* ignore */ }
    setIsValidating(false);

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, record]);

  // --- Build details payload from form data ---
  const buildDetailsPayload = useCallback((): RatePlanDetailEntry[] => {
    return formData.mealPlanDetails
      .filter(r => r.included)
      .map(r => ({
        detailId: r.detailId || undefined,
        mealPlanId: r.mealPlanId,
        mealAdultRate: r.mealAdultRate ? parseFloat(r.mealAdultRate) : 0,
        mealChildRate: r.mealChildRate ? parseFloat(r.mealChildRate) : 0,
        isDefaultMealPlan: r.isDefault,
        rowVersion: r.rowVersion,
      }));
  }, [formData.mealPlanDetails]);

  // --- Confirmed save ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const details = buildDetailsPayload();
      const payload = {
        propertyId: formData.propertyId || undefined,
        roomCategoryId: formData.roomCategoryId || undefined,
        rateType: formData.rateType || undefined,
        rateName: formData.rateName.trim(),
        baseRate: formData.baseRate ? parseFloat(formData.baseRate) : undefined,
        minimumRate: formData.minimumRate ? parseFloat(formData.minimumRate) : undefined,
        extraAdultRate: formData.extraAdultRate ? parseFloat(formData.extraAdultRate) : undefined,
        extraChildRate: formData.extraChildRate ? parseFloat(formData.extraChildRate) : undefined,
        childAgeLimit: formData.childAgeLimit ? parseInt(formData.childAgeLimit, 10) : undefined,
        chargeableChildAge: formData.chargeableChildAge ? parseInt(formData.chargeableChildAge, 10) : undefined,
        infantAgeLimit: formData.infantAgeLimit ? parseInt(formData.infantAgeLimit, 10) : undefined,
        validFrom: formData.validFrom || undefined,
        validTo: formData.validTo || undefined,
        dayOfWeek: formData.dayOfWeek.length > 0 ? formData.dayOfWeek.join(',') : undefined,
        minNights: formData.minNights ? parseInt(formData.minNights, 10) : undefined,
        maxNights: formData.maxNights ? parseInt(formData.maxNights, 10) : undefined,
        advanceBookingDays: formData.advanceBookingDays ? parseInt(formData.advanceBookingDays, 10) : undefined,
        closeToArrival: formData.closeToArrival,
        closeToDeparture: formData.closeToDeparture,
        includeTaxes: formData.includeTaxes,
        isActive: formData.isActive,
        details,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await ratePlanService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateRatePlanRequest);
      } else {
        await ratePlanService.create(payload as CreateRatePlanRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `"${formData.rateName}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave, buildDetailsPayload]);

  // --- Refresh record from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await ratePlanService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data: RatePlanFormData = {
          rateName: rec.rateName || '',
          rateType: rec.rateType || '',
          propertyId: rec.propertyId || '',
          roomCategoryId: rec.roomCategoryId || '',
          baseRate: rec.baseRate != null ? String(rec.baseRate) : '',
          minimumRate: rec.minimumRate != null ? String(rec.minimumRate) : '',
          extraAdultRate: rec.extraAdultRate != null ? String(rec.extraAdultRate) : '',
          extraChildRate: rec.extraChildRate != null ? String(rec.extraChildRate) : '',
          childAgeLimit: rec.childAgeLimit != null ? String(rec.childAgeLimit) : '',
          chargeableChildAge: rec.chargeableChildAge != null ? String(rec.chargeableChildAge) : '',
          infantAgeLimit: rec.infantAgeLimit != null ? String(rec.infantAgeLimit) : '',
          validFrom: rec.validFrom ? rec.validFrom.split('T')[0] : '',
          validTo: rec.validTo ? rec.validTo.split('T')[0] : '',
          dayOfWeek: rec.dayOfWeek ? rec.dayOfWeek.split(',').map(d => d.trim()) : [],
          minNights: rec.minNights != null ? String(rec.minNights) : '',
          maxNights: rec.maxNights != null ? String(rec.maxNights) : '',
          advanceBookingDays: rec.advanceBookingDays != null ? String(rec.advanceBookingDays) : '',
          closeToArrival: rec.closeToArrival ?? false,
          closeToDeparture: rec.closeToDeparture ?? false,
          includeTaxes: rec.includeTaxes ?? false,
          isActive: rec.isActive,
          mealPlanDetails: (rec.details || []).map(d => ({
            mealPlanId: d.mealPlanId,
            mealPlanCode: d.mealPlanCode || '',
            mealPlanName: d.mealPlanName || '',
            included: true,
            mealAdultRate: String(d.mealAdultRate ?? 0),
            mealChildRate: String(d.mealChildRate ?? 0),
            isDefault: d.isDefaultMealPlan,
            detailId: d.id,
            rowVersion: d.rowVersion,
          })),
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        setRulesOpen(!!(data.minNights || data.maxNights || data.advanceBookingDays ||
          data.closeToArrival || data.closeToDeparture || data.includeTaxes));
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  if (!isOpen) return null;

  const includedCount = formData.mealPlanDetails.filter(r => r.included).length;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem'
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'edit' ? 'Saving...' : 'Creating...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Rate Plan' : mode === 'copy' ? 'Copy Rate Plan' : 'Edit Rate Plan'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving || isValidating}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving || isValidating}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Rate Name & Type --- */}
          <div className="form-grid-2">
            <div className="form-group" data-field="rateName">
              <label className="form-label">Rate Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('rateName') ? ' form-field-error' : ''}`}
                value={formData.rateName}
                onChange={(e) => { clearFieldError('rateName'); setFormData(prev => ({ ...prev, rateName: e.target.value })); }}
                placeholder="e.g., Standard Rack Rate"
                maxLength={100}
                autoFocus={mode === 'create' || mode === 'copy'}
              />
              {getFieldError('rateName') && <div className="form-error-message">{getFieldError('rateName')}</div>}
            </div>

            <div className="form-group" data-field="rateType">
              <SyutSelect
                label={<>Rate Type <span className="text-danger">*</span></>}
                options={RATE_TYPE_OPTIONS}
                value={formData.rateType}
                onChange={(val) => { clearFieldError('rateType'); setFormData(prev => ({ ...prev, rateType: (val as string) || '' })); }}
                placeholder="Select rate type..."
                isClearable={false}
                size="md"
                error={getFieldError('rateType')}
              />
            </div>
          </div>

          {/* --- Property, Room Category, Status --- */}
          <div className="form-grid-3">
            <div className="form-group" data-field="propertyId">
              <SyutSelect
                label={<>Property <span className="text-danger">*</span></>}
                options={propertyOptions}
                value={formData.propertyId}
                onChange={(val) => { clearFieldError('propertyId'); setFormData(prev => ({ ...prev, propertyId: (val as string) || '' })); }}
                placeholder="Select property..."
                isClearable={false}
                size="md"
                menuPortalTarget={document.body}
                error={getFieldError('propertyId')}
              />
            </div>

            <div className="form-group" data-field="roomCategoryId">
              <SyutSelect
                label={<>Room Category <span className="text-danger">*</span></>}
                options={roomCategoryOptions}
                value={formData.roomCategoryId}
                onChange={(val) => { clearFieldError('roomCategoryId'); setFormData(prev => ({ ...prev, roomCategoryId: (val as string) || '' })); }}
                placeholder="Select room category..."
                isClearable={false}
                size="md"
                menuPortalTarget={document.body}
                error={getFieldError('roomCategoryId')}
              />
            </div>

            <div className="form-group" data-field="isActive">
              <SyutSelect
                label="Status"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
                value={String(formData.isActive)}
                onChange={(val) => setFormData(prev => ({ ...prev, isActive: val === 'true' }))}
                isClearable={false}
                size="md"
              />
            </div>
          </div>

          {/* --- Rates Section (Room-level) --- */}
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Room Rates</h3>
            <div className="form-grid-4">
              <div className="form-group" data-field="baseRate">
                <label className="form-label">Base Rate <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className={`form-input${getFieldError('baseRate') ? ' form-field-error' : ''}`}
                  value={formData.baseRate}
                  onChange={(e) => { clearFieldError('baseRate'); setFormData(prev => ({ ...prev, baseRate: e.target.value })); }}
                  placeholder="0.00" min="0" step="0.01"
                />
                {getFieldError('baseRate') && <div className="form-error-message">{getFieldError('baseRate')}</div>}
              </div>

              <div className="form-group" data-field="minimumRate">
                <label className="form-label">Minimum Rate</label>
                <input
                  type="number"
                  className={`form-input${getFieldError('minimumRate') ? ' form-field-error' : ''}`}
                  value={formData.minimumRate}
                  onChange={(e) => { clearFieldError('minimumRate'); setFormData(prev => ({ ...prev, minimumRate: e.target.value })); }}
                  placeholder="0.00" min="0" step="0.01"
                />
                {getFieldError('minimumRate') && <div className="form-error-message">{getFieldError('minimumRate')}</div>}
              </div>

              <div className="form-group" data-field="extraAdultRate">
                <label className="form-label">Extra Adult Rate</label>
                <input
                  type="number"
                  className={`form-input${getFieldError('extraAdultRate') ? ' form-field-error' : ''}`}
                  value={formData.extraAdultRate}
                  onChange={(e) => { clearFieldError('extraAdultRate'); setFormData(prev => ({ ...prev, extraAdultRate: e.target.value })); }}
                  placeholder="0.00" min="0" step="0.01"
                />
                {getFieldError('extraAdultRate') && <div className="form-error-message">{getFieldError('extraAdultRate')}</div>}
              </div>

              <div className="form-group" data-field="extraChildRate">
                <label className="form-label">Extra Child Rate</label>
                <input
                  type="number"
                  className={`form-input${getFieldError('extraChildRate') ? ' form-field-error' : ''}`}
                  value={formData.extraChildRate}
                  onChange={(e) => { clearFieldError('extraChildRate'); setFormData(prev => ({ ...prev, extraChildRate: e.target.value })); }}
                  placeholder="0.00" min="0" step="0.01"
                />
                {getFieldError('extraChildRate') && <div className="form-error-message">{getFieldError('extraChildRate')}</div>}
              </div>
            </div>
          </div>

          {/* --- Meal Plan Rates Section (Detail-level) --- */}
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }} data-field="mealPlanDetails">
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
              Meal Plan Rates
              {includedCount > 0 && <span style={{ fontWeight: 400, fontSize: '0.75rem', marginLeft: '0.5rem', color: 'var(--text-tertiary)' }}>({includedCount} selected)</span>}
            </h3>
            {getFieldError('mealPlanDetails') && (
              <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{getFieldError('mealPlanDetails')}</div>
            )}
            {formData.mealPlanDetails.length === 0 ? (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', padding: '0.75rem 0' }}>No meal plans available. Create meal plans first.</div>
            ) : (
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.375rem', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', width: '3.5rem', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Include</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Meal Plan</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', width: '8rem', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Meal Adult Rate</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', width: '8rem', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Meal Child Rate</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', width: '4rem', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.mealPlanDetails.map((row, idx) => (
                      <tr
                        key={row.mealPlanId}
                        style={{
                          borderBottom: idx < formData.mealPlanDetails.length - 1 ? '1px solid var(--border-color)' : 'none',
                          opacity: row.included ? 1 : 0.5,
                          background: row.included ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                        }}
                      >
                        <td style={{ padding: '0.375rem 0.75rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={row.included}
                            onChange={() => handleToggleInclude(row.mealPlanId)}
                            style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)', cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '0.375rem 0.75rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                          <span style={{ fontWeight: 600 }}>{row.mealPlanCode}</span>
                          <span style={{ color: 'var(--text-secondary)', marginLeft: '0.375rem' }}>- {row.mealPlanName}</span>
                        </td>
                        <td style={{ padding: '0.375rem 0.75rem' }}>
                          <input
                            type="number"
                            className="form-input"
                            value={row.mealAdultRate}
                            onChange={(e) => handleDetailRateChange(row.mealPlanId, 'mealAdultRate', e.target.value)}
                            placeholder="0.00" min="0" step="0.01"
                            disabled={!row.included}
                            style={{ textAlign: 'right', height: '1.75rem', fontSize: '0.8rem', padding: '0 0.5rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.375rem 0.75rem' }}>
                          <input
                            type="number"
                            className="form-input"
                            value={row.mealChildRate}
                            onChange={(e) => handleDetailRateChange(row.mealPlanId, 'mealChildRate', e.target.value)}
                            placeholder="0.00" min="0" step="0.01"
                            disabled={!row.included}
                            style={{ textAlign: 'right', height: '1.75rem', fontSize: '0.8rem', padding: '0 0.5rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.375rem 0.75rem', textAlign: 'center' }}>
                          <input
                            type="radio"
                            name="defaultMealPlan"
                            checked={row.isDefault}
                            onChange={() => handleSetDefault(row.mealPlanId)}
                            disabled={!row.included}
                            style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)', cursor: row.included ? 'pointer' : 'default' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* --- Age Configuration --- */}
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Age Configuration</h3>
            <div className="form-grid-3">
              <div className="form-group" data-field="childAgeLimit">
                <label className="form-label">Child Age Limit</label>
                <input type="number" className={`form-input${getFieldError('childAgeLimit') ? ' form-field-error' : ''}`}
                  value={formData.childAgeLimit}
                  onChange={(e) => { clearFieldError('childAgeLimit'); setFormData(prev => ({ ...prev, childAgeLimit: e.target.value })); }}
                  placeholder="12" min="0" step="1"
                />
                {getFieldError('childAgeLimit') && <div className="form-error-message">{getFieldError('childAgeLimit')}</div>}
              </div>
              <div className="form-group" data-field="chargeableChildAge">
                <label className="form-label">Chargeable Child Age</label>
                <input type="number" className={`form-input${getFieldError('chargeableChildAge') ? ' form-field-error' : ''}`}
                  value={formData.chargeableChildAge}
                  onChange={(e) => { clearFieldError('chargeableChildAge'); setFormData(prev => ({ ...prev, chargeableChildAge: e.target.value })); }}
                  placeholder="6" min="0" step="1"
                />
                {getFieldError('chargeableChildAge') && <div className="form-error-message">{getFieldError('chargeableChildAge')}</div>}
              </div>
              <div className="form-group" data-field="infantAgeLimit">
                <label className="form-label">Infant Age Limit</label>
                <input type="number" className={`form-input${getFieldError('infantAgeLimit') ? ' form-field-error' : ''}`}
                  value={formData.infantAgeLimit}
                  onChange={(e) => { clearFieldError('infantAgeLimit'); setFormData(prev => ({ ...prev, infantAgeLimit: e.target.value })); }}
                  placeholder="2" min="0" step="1"
                />
                {getFieldError('infantAgeLimit') && <div className="form-error-message">{getFieldError('infantAgeLimit')}</div>}
              </div>
            </div>
          </div>

          {/* --- Validity & Restrictions --- */}
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Validity & Restrictions</h3>
            <div className="form-grid-2">
              <div className="form-group" data-field="validFrom">
                <label className="form-label">Valid From</label>
                <input type="date" className={`form-input${getFieldError('validFrom') ? ' form-field-error' : ''}`}
                  value={formData.validFrom}
                  onChange={(e) => { clearFieldError('validFrom'); setFormData(prev => ({ ...prev, validFrom: e.target.value })); }}
                />
                {getFieldError('validFrom') && <div className="form-error-message">{getFieldError('validFrom')}</div>}
              </div>
              <div className="form-group" data-field="validTo">
                <label className="form-label">Valid To</label>
                <input type="date" className={`form-input${getFieldError('validTo') ? ' form-field-error' : ''}`}
                  value={formData.validTo}
                  onChange={(e) => { clearFieldError('validTo'); setFormData(prev => ({ ...prev, validTo: e.target.value })); }}
                />
                {getFieldError('validTo') && <div className="form-error-message">{getFieldError('validTo')}</div>}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', marginTop: '0.5rem' }}>
              <div className="form-group" data-field="dayOfWeek">
                <SyutSelect
                  label="Applicable Days"
                  options={DAY_OPTIONS}
                  value={formData.dayOfWeek}
                  onChange={(val) => setFormData(prev => ({ ...prev, dayOfWeek: (val as string[]) || [] }))}
                  placeholder="Select applicable days..."
                  isMulti
                  isClearable
                  size="md"
                />
              </div>
            </div>

            {/* --- Booking Rules ▾ — collapsible. Holds the rarely-set min/max
                  stay caps, advance-booking lead-days, and the close-to-arrival /
                  departure / tax-inclusive flags. None are required. --- */}
            <div style={{ marginTop: '0.75rem', borderTop: '1px dashed var(--border-color)', paddingTop: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setRulesOpen(o => !o)}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)',
                  display: 'flex', alignItems: 'center', gap: '0.375rem', padding: 0,
                }}
              >
                <IconSystem.chevronRight size={14} style={{ transform: rulesOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                Booking Rules
                <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 400 }}>(min/max stay, lead days, arrival/departure flags — optional)</span>
              </button>

              {rulesOpen && (
                <>
                  <div className="form-grid-3" style={{ marginTop: '0.5rem' }}>
                    <div className="form-group" data-field="minNights">
                      <label className="form-label">Min Nights</label>
                      <input type="number" className={`form-input${getFieldError('minNights') ? ' form-field-error' : ''}`}
                        value={formData.minNights}
                        onChange={(e) => { clearFieldError('minNights'); setFormData(prev => ({ ...prev, minNights: e.target.value })); }}
                        placeholder="1" min="0" step="1"
                      />
                      {getFieldError('minNights') && <div className="form-error-message">{getFieldError('minNights')}</div>}
                    </div>
                    <div className="form-group" data-field="maxNights">
                      <label className="form-label">Max Nights</label>
                      <input type="number" className={`form-input${getFieldError('maxNights') ? ' form-field-error' : ''}`}
                        value={formData.maxNights}
                        onChange={(e) => { clearFieldError('maxNights'); setFormData(prev => ({ ...prev, maxNights: e.target.value })); }}
                        placeholder="30" min="0" step="1"
                      />
                      {getFieldError('maxNights') && <div className="form-error-message">{getFieldError('maxNights')}</div>}
                    </div>
                    <div className="form-group" data-field="advanceBookingDays">
                      <label className="form-label">Advance Booking Days</label>
                      <input type="number" className={`form-input${getFieldError('advanceBookingDays') ? ' form-field-error' : ''}`}
                        value={formData.advanceBookingDays}
                        onChange={(e) => { clearFieldError('advanceBookingDays'); setFormData(prev => ({ ...prev, advanceBookingDays: e.target.value })); }}
                        placeholder="0" min="0" step="1"
                      />
                      {getFieldError('advanceBookingDays') && <div className="form-error-message">{getFieldError('advanceBookingDays')}</div>}
                    </div>
                  </div>

                  <div className="form-grid-3" style={{ marginTop: '0.5rem' }}>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', minHeight: '2.25rem' }}>
                        <input type="checkbox" checked={formData.closeToArrival}
                          onChange={(e) => setFormData(prev => ({ ...prev, closeToArrival: e.target.checked }))}
                          style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
                        />
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Close to Arrival</span>
                      </label>
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', minHeight: '2.25rem' }}>
                        <input type="checkbox" checked={formData.closeToDeparture}
                          onChange={(e) => setFormData(prev => ({ ...prev, closeToDeparture: e.target.checked }))}
                          style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
                        />
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Close to Departure</span>
                      </label>
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', minHeight: '2.25rem' }}>
                        <input type="checkbox" checked={formData.includeTaxes}
                          onChange={(e) => setFormData(prev => ({ ...prev, includeTaxes: e.target.checked }))}
                          style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
                        />
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Tax Inclusive</span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving || isValidating}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || isValidating || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Rate Plan'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the rate plan "${formData.rateName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default RatePlanModal;
